// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/__tests__/view.test.tsx
import { Box, Flex, Grid, Tabs } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ActivityPanel } from "./components/ActivityPanel";
import { KnowledgeDatabaseHeader } from "./components/KnowledgeDatabaseHeader";
import { MemoriesPanel } from "./components/MemoriesPanel";
import { RulesPanel } from "./components/RulesPanel";
import { SourcesPanel } from "./components/SourcesPanel";
import { knowledgeDatabaseRoute, type KnowledgeTab } from "./route";

/**
 * Refreshed Knowledge Database: four fixed tabs. Sources first (uploads +
 * per-document lifecycle status — the landing tab), Rules (extracted from
 * uploaded guidelines; suggestions live behind a status filter, not a fifth
 * tab), the read-only Memories view, and the append-only Activity audit
 * trail. Pure react-query per panel — no Redux slice, no route loader.
 */
export default function KnowledgeDatabaseView() {
  const { t } = useTranslation("ui");
  const { tab, q } = knowledgeDatabaseRoute.useSearch();
  const navigate = knowledgeDatabaseRoute.useNavigate();

  // Radix Tabs fires onValueChange on mousedown (even for the already-active
  // trigger) AND again on focus before the router has flushed the search
  // update, so an unguarded handler dispatches duplicate navigations. Track
  // the last dispatched target and re-sync when the route search changes
  // (covers external navigations too).
  const lastTabTargetRef = useRef<KnowledgeTab>(tab);
  useEffect(() => {
    lastTabTargetRef.current = tab;
  }, [tab]);

  function handleTabChange(nextTab: string) {
    const target = nextTab as KnowledgeTab;
    if (lastTabTargetRef.current === target) return;
    lastTabTargetRef.current = target;
    navigate({
      search: (prev) => ({
        ...prev,
        tab: target,
        // Deep-link params are tab-local; drop them on tab switch.
        ruleId: undefined,
        sourceId: undefined,
        cite: undefined,
      }),
    });
  }

  return (
    <Box
      className="h-full overflow-auto px-8 py-2"
      data-testid="knowledge-database-page"
    >
      <Flex direction="column" className="px-2 py-4">
        <KnowledgeDatabaseHeader />
      </Flex>
      <Grid
        className={cx("grid-rows-[auto_1fr] overflow-auto")}
        px="2"
        data-testid="knowledge-database-content"
      >
        <Tabs.Root
          value={tab}
          onValueChange={handleTabChange}
          data-testid="knowledge-database-tabs"
        >
          <Tabs.List
            className="flex-wrap items-center shadow-none"
            data-testid="knowledge-database-tabs-list"
          >
            <Tabs.Trigger
              value="sources"
              data-testid="knowledge-database-tab-sources"
            >
              {t("settings.view.sources")}
            </Tabs.Trigger>
            <Tabs.Trigger
              value="rules"
              data-testid="knowledge-database-tab-rules"
            >
              {t("settings.view.rules")}
            </Tabs.Trigger>
            <Tabs.Trigger
              value="memories"
              data-testid="knowledge-database-tab-memories"
            >
              {t("settings.view.memories")}
            </Tabs.Trigger>
            <Tabs.Trigger
              value="activity"
              data-testid="knowledge-database-tab-activity"
            >
              {t("settings.view.activity")}
            </Tabs.Trigger>
          </Tabs.List>

          <Box pt="3" data-testid="knowledge-database-tab-content">
            <Tabs.Content value="sources">
              <SourcesPanel />
            </Tabs.Content>
            <Tabs.Content value="rules">
              <RulesPanel />
            </Tabs.Content>
            <Tabs.Content value="memories">
              {/*
                A memory citation deep-links here with `?tab=memories&q=...`;
                the panel keeps the term in local state, so it is seeded, not
                controlled. RulesPanel reads `q` off the route itself — the
                memories arm goes through a prop so the panel stays route-free.
              */}
              <MemoriesPanel initialQuery={q} />
            </Tabs.Content>
            <Tabs.Content value="activity">
              <ActivityPanel />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Grid>
    </Box>
  );
}
